import {
  Edit,
  Trash2,
  Users,
  Settings,
  RefreshCw,
  BookOpen,
  GraduationCap,
  Users2,
  History,
  ArrowRight,
  Database,
  FileText
} from 'lucide-react'
import {
  Button,
  Tile,
  TileContent,
  TileDescription,
  TileFooter,
  TileHeader,
  TileTitle
} from './ui'

interface UnitCardProps {
  unit: any
  unitType: 'course' | 'group' | 'class'
  onEdit: (unit: any) => void
  onDelete: (id: number) => void
  onParticipants: (unit: any) => void
  onOwners?: (unit: any) => void
  onStudentManagement?: (unit: any) => void
  onHistory?: (unit: any) => void
  onTransitions?: (unit: any) => void
  onMetadata?: (unit: any) => void
  onPedagogicalNotes?: (unit: any) => void
  showTrash?: boolean
  onRestore?: (id: number) => void
}

export default function UnitCard({
  unit,
  unitType,
  onEdit,
  onDelete,
  onParticipants,
  onOwners,
  onStudentManagement,
  onHistory,
  onTransitions,
  onMetadata,
  onPedagogicalNotes,
  showTrash = false,
  onRestore
}: UnitCardProps) {
  const getIcon = () => {
    if (unitType === 'class') return GraduationCap
    if (unitType === 'course') return BookOpen
    return Users2
  }

  const Icon = getIcon()

  const getUnitName = () => {
    if (unitType === 'class') return unit.class_name
    return unit.name
  }

  const getUnitSubtitle = () => {
    if (unitType === 'class') {
      return unit.grade_level || unit.department || ''
    }
    if (unitType === 'course') {
      return unit.fach || unit.jahrgang || ''
    }
    if (unitType === 'group') {
      return unit.gruppen_typ || ''
    }
    return ''
  }

  const getMemberCount = () => {
    return unit.active_members_count || unit.teilnehmer_anzahl || 0
  }

  const getMaxMembers = () => {
    return unit.max_teilnehmer || unit.max_students || (unitType === 'course' ? 30 : 10)
  }

  return (
    <Tile>
      <TileHeader className="flex items-start gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
          style={{ backgroundColor: unit.farbe || '#3B82F6' }}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0">
          <TileTitle className="text-base">{getUnitName()}</TileTitle>
          {getUnitSubtitle() ? <TileDescription>{getUnitSubtitle()}</TileDescription> : null}
        </div>
      </TileHeader>
      <TileContent className="space-y-1 text-sm">
        <p className="text-gray-600 dark:text-gray-400">
          Teilnehmer:{' '}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {getMemberCount()}/{getMaxMembers()}
          </span>
        </p>
        {unitType === 'course' && unit.jahrgang && (
          <p className="text-gray-600 dark:text-gray-400">
            Jahrgang:{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{unit.jahrgang}</span>
          </p>
        )}
        {unitType === 'class' && unit.grade_level && (
          <p className="text-gray-600 dark:text-gray-400">
            Stufe:{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{unit.grade_level}</span>
          </p>
        )}
      </TileContent>
      <TileFooter className="flex flex-wrap gap-1">
        {showTrash ? (
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => onRestore && onRestore(unit.id)}
            title="Wiederherstellen"
            className="!px-1.5 text-green-700 dark:text-green-400"
          >
            <RefreshCw className="h-3 w-3" aria-hidden />
          </Button>
        ) : (
          <>
            <Button type="button" size="xs" variant="outline" onClick={() => onEdit(unit)} title="Bearbeiten" className="!px-1.5">
              <Edit className="h-3 w-3" aria-hidden />
            </Button>
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => onStudentManagement && onStudentManagement(unit)}
              title="Schüler verwalten"
              className="!px-1.5 text-blue-700 dark:text-blue-400"
            >
              <Users className="h-3 w-3" aria-hidden />
            </Button>
            {onHistory && (
              <Button type="button" size="xs" variant="outline" onClick={() => onHistory(unit)} title="Historie" className="!px-1.5">
                <History className="h-3 w-3" aria-hidden />
              </Button>
            )}
            {onTransitions && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={() => onTransitions(unit)}
                title="Nachfolger festlegen und Übergänge durchführen"
                className="!px-1.5 text-purple-700 dark:text-purple-400"
              >
                <ArrowRight className="h-3 w-3" aria-hidden />
              </Button>
            )}
            {onMetadata && (
              <Button type="button" size="xs" variant="outline" onClick={() => onMetadata(unit)} title="Metadaten" className="!px-1.5">
                <Database className="h-3 w-3" aria-hidden />
              </Button>
            )}
            {onPedagogicalNotes && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={() => onPedagogicalNotes(unit)}
                title="Pädagogische Notizen"
                className="!px-1.5"
              >
                <FileText className="h-3 w-3" aria-hidden />
              </Button>
            )}
            {onOwners && (
              <Button type="button" size="xs" variant="outline" onClick={() => onOwners(unit)} title="Besitzer" className="!px-1.5">
                <Settings className="h-3 w-3" aria-hidden />
              </Button>
            )}
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => onDelete(unit.id)}
              title="Löschen"
              className="!px-1.5 text-red-700 dark:text-red-400"
            >
              <Trash2 className="h-3 w-3" aria-hidden />
            </Button>
          </>
        )}
      </TileFooter>
    </Tile>
  )
}
